import * as React from 'react';
import {
  Box,
  Grid,
  Typography,
  Stack,
  Badge,
  ThemeProvider,
  createTheme,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
  LinearProgress,
  Divider,
  Paper,
  useTheme,
  IconButton,
} from '@mui/material';
import {ArrowCircleRight, Info} from '@mui/icons-material';
import {UserFilled, ChevronDown, ChevronUp} from '@carbon/icons-react';
import {
  IRaci,
  phaseColorsConstants,
  IStep,
  CardType,
  RACI_ClassNames,
  ProcessTabs,
  DocumentCategories,
  documentTypeMappings,
} from 'app/types/Shared';
import {useSelector} from 'react-redux';
import {selectRoleFilters} from 'app/store/appState/appStateSlice';
import ReactMarkdown from 'react-markdown';
import {ArtifactCards} from 'components/ArtifactCards';
import {selectAllMaps} from 'app/store/operatingModel/operatingModelSlice';
import grips from 'assets/images/grips.png';
import {ArtifactCard} from 'components/ArtifactCards/ArtifactCard';
import {StyledTooltip} from 'components/StyledTooltip/StyledTooltip';

//Create theme for RACI Badges. Set Custom Badge colors
const RACITheme = createTheme({
  palette: {
    primary: {
      main: '#ABB436', // change primary color for R
    },
    secondary: {
      main: '#EA5532', // change secondary color for A
    },
    warning: {
      main: '#DA5283', // change waring color for C
    },
    error: {
      main: '#51B1BF', // change error color for I
    },
    info: {
      main: '#d9d9d9', // change primary color for unselected
    },
  },
});

// custom style Badge -- All text color will be white
const StyledBadge = styled(Badge)({
  '& .MuiBadge-badge': {
    color: 'white',
  },
});

const StyledPaper = styled(Paper)({
  //scrollbarWidth: 'none' /* Firefox */,
  maxHeight: 800,
  overflowY: 'scroll',
  '&::-webkit-scrollbar': {
    width: '12px',
    height: '10px',
  } /* Chrome */,
  '&::-webkit-scrollbar-track': {
    background: '#DDDFE3',
    maxWidth: '8px',
  } /* Chrome */,
  '&::-webkit-scrollbar-thumb': {
    backgroundImage: `url(${grips})`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: 'white',
    minHeight: '50%',
    maxHeight: '50%',
    border: '2px solid #DDDFE3',
    minWidth: '12px',
    maxWidth: '12px',
  },
});

// returns the badge color based on RACI letter. See theme above for custom color that are set
const getBadgeColor = (s: string) => {
  if (s === 'R') {
    return 'primary';
  }
  if (s === 'A') {
    return 'secondary';
  }
  if (s === 'C') {
    return 'warning';
  } else {
    return 'error';
  }
};

// type indexProps = {
//   category: string;
//   class1: string;
// };

//Props to pass to each ROW (Convert to store)??
type rowProps = {
  step: IStep; //current step information
  CROSize: number; //size of CRO grid section (for determing grid column for RACI badge placement)
  TAKSize: number; //size of Takeda grid section (for determing grid column for RACI badge placement)
  OTAKSize: number; //size of OTakeda grid section (for determing grid column for RACI badge placement)
  FilterDictionary: {[key: string]: Array<filter>}; //current filters
  onL2Click?: (id: string) => void;
  simplified?: boolean;
  handleTabChange?: (tabIndex: number, tab: ProcessTabs) => void;
  storybook?: boolean;
  showCount: boolean;
};

//Props for Class Filters.
type filter = {
  roleAbbr: string; //Role Name for Filter
  roleName: string;
  selected: boolean; //If filter is selected or not
};

// Rendering a singular RACI Row
const RACIRow = ({
  step,
  showCount,
  CROSize,
  TAKSize,
  OTAKSize,
  onL2Click,
  simplified,
  handleTabChange,
  storybook,
  FilterDictionary,
}: rowProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(false);
  const userRole = useSelector(selectRoleFilters);
  const sops = step.artifacts?.filter((a) => documentTypeMappings[DocumentCategories.SOPs].includes(a.artifact.type));
  const TemplatesAndTools = step.artifacts?.filter((a) =>
    documentTypeMappings[DocumentCategories.ToolsAndForms].includes(a.artifact.type),
  );
  const businessControlled = step.artifacts?.filter((a) =>
    documentTypeMappings[DocumentCategories.BusinessControlled].includes(a.artifact.type),
  );

  const HandleExpand = () => {
    if (expanded) {
      setExpanded(false);
    } else {
      setExpanded(true);
    }
  };

  const setBadgeColor = (type: string, index: number) => {
    //If no Filters are selected color all badges
    if (
      !FilterDictionary[RACI_ClassNames.CRO].some((item) => item.selected === true) &&
      !FilterDictionary[RACI_ClassNames.oTAK].some((item) => item.selected === true) &&
      !FilterDictionary[RACI_ClassNames.TAK].some((item) => item.selected === true)
    ) {
      return true;
    }
    //color only badges who have same class and role type as filters selected
    else {
      return FilterDictionary[type][index]?.selected;
    }
  };

  const stepPhase = (phase: string) => {
    const findColor = phaseColorsConstants.find((phaseItem) => phaseItem.name == phase);
    return (
      <Box
        bgcolor={findColor?.color}
        borderRadius={1}
        width={35}
        height={25}
        display='flex'
        alignItems='center'
        justifyContent='center'
      >
        <Typography color='white' variant='h3'>
          {findColor?.abbr}
        </Typography>
      </Box>
    );
  };

  const stepRole = (roles: Array<IRaci>) => {
    const findRole = roles.some((r) => userRole.some((x) => r.role.role_category.label === x));
    return (
      <Box width={20}>
        {findRole ? (
          <>
            <UserFilled style={{width: '24px', height: '24px'}} />
          </>
        ) : (
          <>
            <Box width={'20px'} />
          </>
        )}
      </Box>
    );
  };

  const RaciColumn = (raci: string, title: string) => {
    const raciArr = raci.split(';');

    const getRaciBadge = (position: number) => {
      if (position === 0 && raciArr.find((r) => r === 'R')) {
        return <StyledBadge badgeContent={'R'} color={getBadgeColor('R')} />;
      } else if (position === 1 && raciArr.find((r) => r === 'A')) {
        return <StyledBadge badgeContent={'A'} color={getBadgeColor('A')} />;
      } else if (position === 2 && raciArr.find((r) => r === 'C')) {
        return <StyledBadge badgeContent={'C'} color={getBadgeColor('C')} />;
      } else if (position === 3 && raciArr.find((r) => r === 'I')) {
        return <StyledBadge badgeContent={'I'} color={getBadgeColor('I')} />;
      } else return <></>;
    };

    return (
      <Grid container direction='row' display='flex' alignItems='center' spacing={3}>
        <Grid item xs={7} textAlign='left'>
          <Typography flexGrow={1} variant='h3' fontWeight={400}>
            {title}
          </Typography>
        </Grid>
        <ThemeProvider theme={RACITheme}>
          <Grid item xs={5} container spacing={4}>
            {[0, 1, 2, 3].map((raciItem, order) => (
              <Grid item xs={1.25} key={`raciItem-${raci}-${order}-${step?.key}`}>
                {getRaciBadge(raciItem)}
              </Grid>
            ))}
          </Grid>
        </ThemeProvider>
      </Grid>
    );
  };

  const RenderThemedBadge = (raciItem: string, selected: boolean) => {
    return (
      <ThemeProvider theme={RACITheme}>
        <StyledBadge badgeContent={raciItem} color={selected ? getBadgeColor(raciItem) : 'info'} />
      </ThemeProvider>
    );
  };

  const getRACI = (type: RACI_ClassNames, index: number) => {
    const roleCat = FilterDictionary[type][index];
    const RACI = step.raci
      ?.filter((role) => role.role.class_name === type && role.role.role_category.abbrev === roleCat.roleAbbr)
      .map((role) => role.raci_list);
    const raciItems = [];
    if (RACI.some((x) => x.includes('R'))) {
      raciItems.push('R');
    }
    if (RACI.some((x) => x.includes('A'))) {
      raciItems.push('A');
    }
    if (RACI.some((x) => x.includes('C'))) {
      raciItems.push('C');
    }
    if (RACI.some((x) => x.includes('I'))) {
      raciItems.push('I');
    }

    return raciItems;
  };

  type ClassGrid = {
    size: number;
    type: RACI_ClassNames;
  };
  const RenderClassGrid = ({size, type}: ClassGrid) => (
    <Grid item xs={size} onClick={() => HandleExpand()} minWidth={100 * size}>
      <Grid container item direction='row' columns={size} height='100%' minWidth={100 * size}>
        {[...Array(size)].map((_, index) => (
          <Grid
            item
            xs={1}
            key={`ARRAY_${type}.${index}`}
            sx={{
              borderLeft: '1px solid',
              borderBottom: '1px solid',
              borderColor: theme.palette.grey[500],
              bgcolor: theme.palette.grey[100],
              justifyContent: 'center',
              alignItems: 'flex-start',
              display: 'flex',
              width: '100%',
              pt: 2,
            }}
          >
            <Box>
              <Grid container direction='column' spacing={2}>
                {getRACI(type, index).map((raciItem) => (
                  <React.Fragment key={`${step?.key}.RACI-item${index}:${type}-${raciItem}`}>
                    <Box width='100%' pt={0.5} pb={0.5} pl={2}>
                      <Grid item>{RenderThemedBadge(raciItem, setBadgeColor(type, index))}</Grid>
                    </Box>
                  </React.Fragment>
                ))}
              </Grid>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
  //Grid size is dynamic. 7 units for the description accordian. CRO, TAK, OTAK are based on how many role filters
  // there are for each class. 7 for comments, 3 for flags
  let columns = 14 + (CROSize + TAKSize + OTAKSize);
  if (showCount) {
    columns += 3;
  }

  return (
    <>
      <Grid
        container
        direction='row'
        columns={columns}
        sx={{cursor: 'pointer'}}
        id={simplified ? step.level_label : step.desc}
      >
        <Grid item xs={7} sx={{borderBottom: 0, minWidth: '25% !important'}} onClick={() => HandleExpand()}>
          <Accordion disableGutters elevation={0} sx={{width: '100%'}} expanded={expanded}>
            <AccordionSummary sx={{borderBottom: 0}}>
              <Stack direction='row' spacing={2} justifyContent='flex-start' alignItems='center'>
                <Box px={0.5}>{stepRole(step.raci)}</Box>
                <Box pb={0.5}>{stepPhase(step.phase_key)}</Box>
                <Typography variant='h5' pb={0.5}>
                  {simplified ? step.level_label : step.desc}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{padding: 1}}>
              {expanded &&
                step.raci.map((item) => (
                  <Box key={`RACI Details-${step?.key}-${item.role.name}`}>
                    <Grid container direction='row' alignContent='center' justifyContent='flex-start' mb={0.5}>
                      <Grid item xs={1} />
                      <Grid item xs={11}>
                        {RaciColumn(item.raci_list, item.role.desc)}
                      </Grid>
                    </Grid>
                    <Divider sx={{mb: 1}} />
                  </Box>
                ))}
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid
          xs={CROSize + TAKSize + OTAKSize}
          className='test-ref'
          item
          style={{
            maxWidth: `calc(75% - max(378px, ${((showCount ? 10 : 7) / columns) * 100}%))`,

            display: 'grid',
            gridAutoFlow: 'column',
            overflowX: 'hidden',
            justifyContent: 'flex-start',
          }}
        >
          {CROSize > 0 && <RenderClassGrid key={RACI_ClassNames.CRO} size={CROSize} type={RACI_ClassNames.CRO} />}
          {OTAKSize > 0 && <RenderClassGrid key={RACI_ClassNames.oTAK} size={OTAKSize} type={RACI_ClassNames.oTAK} />}
          {TAKSize > 0 && <RenderClassGrid key={RACI_ClassNames.TAK} size={TAKSize} type={RACI_ClassNames.TAK} />}
        </Grid>
        {simplified ? (
          <>
            <Grid item xs={5.5} sx={{borderBottom: '1px solid', borderColor: theme.palette.grey[500]}}>
              {step.details && <ReactMarkdown>{step.details}</ReactMarkdown>}
            </Grid>
            <Grid item xs={1.5} sx={{borderBottom: '1px solid', borderColor: theme.palette.grey[500]}}>
              <Stack
                px={1}
                py={0.5}
                onClick={() => {
                  handleTabChange && handleTabChange(0, ProcessTabs.Map);
                  onL2Click && onL2Click(step.level_label ? step.level_label : step.desc);
                }}
              >
                <Typography variant='h6' color='primary'>
                  View in Process Map
                </Typography>
                <ArrowCircleRight fontSize='medium' sx={{color: theme.palette.primary.main}} />
              </Stack>
            </Grid>
          </>
        ) : (
          <Grid
            item
            xs={showCount ? 10 : 7}
            sx={{
              borderBottom: '1px solid',
              borderColor: theme.palette.grey[500],
              // maxWidth: '378px  !important',
              minWidth: 378,
            }}
          >
            <Stack direction='row' justifyContent='flex-end' alignItems='flex-start' pr={2}>
              <Box
                sx={{
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: expanded ? 'none' : 2,
                  my: 1,
                  minHeight: 'auto',
                  flexGrow: 1,
                  pl: 1,
                }}
              >
                <Box onClick={() => HandleExpand()}>
                  {(step.details || step.artifacts) && (
                    <>
                      <Typography variant='h5' fontWeight={700}>
                        Additional Details
                      </Typography>
                      <Typography variant='h6'>Objective or key points about this task</Typography>
                    </>
                  )}
                  {step.details && <ReactMarkdown>{step.details}</ReactMarkdown>}
                </Box>
                {expanded && (
                  <Box>
                    {!!sops?.length && (
                      <React.Fragment>
                        <Typography variant='h5' fontWeight={700} my={1.5}>
                          {DocumentCategories.SOPs}
                        </Typography>

                        <Stack spacing={1}>
                          {sops.map(({artifact}) => (
                            <React.Fragment key={`RACI-${artifact.key}-${step?.key}`}>
                              {storybook ? (
                                <ArtifactCard
                                  pinned={false}
                                  data={{
                                    ...artifact,
                                    created_dt: artifact.created_dt.substring(0, 10),
                                    card: CardType.document,
                                  }}
                                />
                              ) : (
                                <ArtifactCards
                                  //pinned={false}
                                  data={{
                                    ...artifact,
                                    created_dt: artifact.created_dt.substring(0, 10),
                                    card: CardType.document,
                                  }}
                                />
                              )}
                            </React.Fragment>
                          ))}
                        </Stack>
                      </React.Fragment>
                    )}
                    {!!TemplatesAndTools?.length && (
                      <React.Fragment>
                        <Typography variant='h5' fontWeight={700} my={1.5}>
                          {DocumentCategories.ToolsAndForms}
                        </Typography>

                        <Stack spacing={1}>
                          {TemplatesAndTools.map(({artifact}) => (
                            <React.Fragment key={`RACI-${artifact.key}-${step?.key}`}>
                              {storybook ? (
                                <ArtifactCard
                                  pinned={false}
                                  data={{
                                    ...artifact,
                                    created_dt: artifact.created_dt.substring(0, 10),
                                    card: CardType.document,
                                  }}
                                />
                              ) : (
                                <ArtifactCards
                                  //pinned={false}
                                  data={{
                                    ...artifact,
                                    created_dt: artifact.created_dt.substring(0, 10),
                                    card: CardType.document,
                                  }}
                                />
                              )}
                            </React.Fragment>
                          ))}
                        </Stack>
                      </React.Fragment>
                    )}
                    {!!businessControlled?.length && (
                      <React.Fragment>
                        <Stack spacing={1} direction='row' alignItems='center'>
                          <Typography variant='h5' fontWeight={700} my={1.5}>
                            {DocumentCategories.BusinessControlled}
                          </Typography>
                          <StyledTooltip
                            title={
                              <Typography variant='h6'>
                                Non-QMS documents maintained by the business that outline best practices, guidance, tip
                                sheets, and other supportive information for business processes and systems.
                              </Typography>
                            }
                          >
                            <IconButton sx={{p: 0}} disableFocusRipple disableRipple>
                              <Info fontSize='small' sx={{color: theme.palette.grey[600]}} />
                            </IconButton>
                          </StyledTooltip>
                        </Stack>
                        <Stack spacing={1} pb={0.5}>
                          {businessControlled.map(({artifact}) => (
                            <React.Fragment key={`RACI-${artifact.key}-${step?.key}`}>
                              {storybook ? (
                                <ArtifactCard
                                  pinned={false}
                                  data={{
                                    ...artifact,
                                    created_dt: artifact.created_dt.substring(0, 10),
                                    card: CardType.document,
                                  }}
                                />
                              ) : (
                                <ArtifactCards
                                  //pinned={false}
                                  data={{
                                    ...artifact,
                                    created_dt: artifact.created_dt.substring(0, 10),
                                    card: CardType.document,
                                  }}
                                />
                              )}
                            </React.Fragment>
                          ))}
                        </Stack>
                      </React.Fragment>
                    )}
                  </Box>
                )}
              </Box>
              {expanded ? (
                <Box width='20px' height='100%' display='flex' alignItems='flex-start' marginTop={2}>
                  <ChevronUp
                    width='20px'
                    height='20px'
                    style={{color: theme.palette.grey[800], marginTop: 2, marginBottom: 2}}
                    onClick={() => HandleExpand()}
                  />
                </Box>
              ) : (
                <Box width='20px' height='100%' marginTop={2}>
                  <ChevronDown
                    width='20px'
                    height='20px'
                    style={{color: theme.palette.grey[800], marginTop: 2, marginBottom: 2}}
                    onClick={() => HandleExpand()}
                  />
                </Box>
              )}
            </Stack>
          </Grid>
        )}
      </Grid>
    </>
  );
};

//style Accordian for Bucket. No padding, grid takes up full width
const AccordionDetailsStyled = styled(AccordionDetails)(({theme}) => ({
  padding: theme.spacing(0),
  paddingTop: 3,
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

//Props for each bucket
type BucketProps = {
  title: string; //process title
  items: Array<IStep>;
  FilterDictionary: {[key: string]: Array<filter>}; //role filters. Key = Class
  CROSize: number; //CRO grid section Size
  TAKSize: number; //TAK grid section Size
  OTAKSize: number; //OTAK grid section Size
  bucket: string; //bucket string name
  onL1Click?: (subprocess: number, label: string) => void;
  onL2Click?: (id: string) => void;
  handleTabChange?: (tabIndex: number, tab: ProcessTabs) => void;
  simplified?: boolean;
  storybook?: boolean;
  showCount: boolean;
};

//RACI Bucket. Accordian for raci rows (I.E. Level 1 --> Master, Site, Country, etc..)
const RACIBucket = ({
  showCount,
  title,
  items,
  FilterDictionary,
  CROSize,
  TAKSize,
  OTAKSize,
  bucket,
  onL1Click,
  onL2Click,
  handleTabChange,
  simplified,
  storybook,
}: BucketProps) => {
  const theme = useTheme();
  const disabled = bucket === title ? false : true;
  const diagrams = useSelector(selectAllMaps);
  const handleClick = () => {
    if (onL1Click) {
      const l1_order = diagrams?.find((x) => x.l1_label === title)?.l1_label_order;
      if (l1_order) {
        onL1Click(l1_order, title);
      }
    }
  };

  if (CROSize + TAKSize + OTAKSize === 0) {
    return (
      <Box sx={{pt: 10, width: '100%'}}>
        <LinearProgress color='secondary' />
      </Box>
    );
  }

  return (
    <>
      {!items ? (
        <>
          <Box sx={{pt: 10, width: '100%'}}>
            <LinearProgress color='secondary' />
          </Box>
        </>
      ) : (
        <>
          <Accordion
            disableGutters
            elevation={0}
            expanded={!disabled}
            sx={{
              cursor: 'pointer',
            }}
          >
            <AccordionSummary
              expandIcon={
                <ChevronDown
                  fontSize='large'
                  style={{
                    color: 'white',
                  }}
                />
              }
              sx={{
                bgcolor: disabled ? 'grey' : theme.palette.grey[800],
                opacity: disabled ? '0.8' : 'none',
                flexDirection: 'row-reverse',
              }}
              onClick={() => handleClick()}
            >
              <Typography color='white' variant='h4' marginLeft={1}>
                {title}
              </Typography>
            </AccordionSummary>

            <AccordionDetailsStyled>
              <StyledPaper>
                {bucket === title &&
                  items.map((item) => (
                    <Box key={`Row:${item.order}-${item?.key}${Math.random()}`} id={item.desc}>
                      <>
                        {item.level === 2 && !simplified ? (
                          <Box
                            width='100%'
                            height={50}
                            bgcolor={theme.palette.grey[200]}
                            display='flex'
                            justifyContent='flex-start'
                            alignItems='center'
                            id={item.level_label}
                          >
                            <Typography variant='h5' pl={2} fontWeight={600}>
                              {item.level_label}
                            </Typography>
                          </Box>
                        ) : (
                          <RACIRow
                            showCount={showCount}
                            step={item}
                            FilterDictionary={FilterDictionary}
                            CROSize={CROSize}
                            TAKSize={TAKSize}
                            OTAKSize={OTAKSize}
                            onL2Click={onL2Click}
                            simplified={simplified}
                            handleTabChange={handleTabChange}
                            storybook={storybook}
                          />
                        )}
                      </>
                    </Box>
                  ))}
              </StyledPaper>
            </AccordionDetailsStyled>
          </Accordion>
        </>
      )}
    </>
  );
};

export default RACIBucket;
